import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';

type SwipeDirection = ComponentProps<typeof BaseDrawer.Root>['swipeDirection'];

/**
 * Per edge: where the sheet sits, how big it is, which way a swipe dismisses it,
 * and where it rests while closed.
 *
 * Every class here is spelled out rather than built from the side at runtime,
 * because Tailwind only generates utilities it can find in the source text.
 */
const sides: Record<
  DrawerSide,
  { swipeDirection: SwipeDirection; viewport: string; popup: string; closed: string }
> = {
  right: {
    swipeDirection: 'right',
    viewport: 'items-stretch justify-end',
    popup:
      'h-full w-[min(24rem,calc(100vw-3rem))] border-l [transform:translateX(var(--drawer-swipe-movement-x))]',
    closed:
      'data-starting-style:[transform:translateX(100%)] data-ending-style:[transform:translateX(100%)]',
  },
  left: {
    swipeDirection: 'left',
    viewport: 'items-stretch justify-start',
    popup:
      'h-full w-[min(24rem,calc(100vw-3rem))] border-r [transform:translateX(var(--drawer-swipe-movement-x))]',
    closed:
      'data-starting-style:[transform:translateX(-100%)] data-ending-style:[transform:translateX(-100%)]',
  },
  bottom: {
    swipeDirection: 'down',
    viewport: 'flex-col items-stretch justify-end',
    popup:
      'max-h-[min(32rem,calc(100dvh-3rem))] w-full rounded-t-card border-t [transform:translateY(var(--drawer-swipe-movement-y))]',
    closed:
      'data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]',
  },
  top: {
    swipeDirection: 'up',
    viewport: 'flex-col items-stretch justify-start',
    popup:
      'max-h-[min(32rem,calc(100dvh-3rem))] w-full rounded-b-card border-b [transform:translateY(var(--drawer-swipe-movement-y))]',
    closed:
      'data-starting-style:[transform:translateY(-100%)] data-ending-style:[transform:translateY(-100%)]',
  },
};

export interface DrawerProps
  extends Omit<ComponentProps<typeof BaseDrawer.Root>, 'swipeDirection'> {
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Which edge the sheet comes from. Also fixes the direction a swipe dismisses it. */
  side?: DrawerSide;
  className?: string;
}

/**
 * A sheet that slides in from an edge and can be swiped away.
 *
 * The backdrop's opacity and the sheet's offset both track `--drawer-swipe-*`,
 * so a half-finished drag looks half-finished rather than snapping between two
 * states — that feedback is the reason to use a drawer over a {@link Dialog}.
 */
export function Drawer({
  trigger,
  title,
  description,
  children,
  side = 'right',
  className,
  ...rootProps
}: DrawerProps) {
  const edge = sides[side];

  return (
    <BaseDrawer.Root swipeDirection={edge.swipeDirection} {...rootProps}>
      {trigger ? <BaseDrawer.Trigger render={trigger as never} /> : null}
      <BaseDrawer.Portal>
        <BaseDrawer.Backdrop
          className={cx(
            'fixed inset-0 min-h-dvh bg-scrim backdrop-blur-[4px]',
            'opacity-[calc(1-var(--drawer-swipe-progress))]',
            'transition-opacity duration-300 ease-out-quint data-swiping:duration-0',
            'data-starting-style:opacity-0 data-ending-style:opacity-0',
          )}
        />
        <BaseDrawer.Viewport className={cx('fixed inset-0 flex', edge.viewport)}>
          <BaseDrawer.Popup
            className={cx(
              'overflow-y-auto overscroll-contain border-border-subtle bg-surface-raised p-6 text-ink shadow-modal outline-none',
              'transition-transform duration-300 ease-out-quint data-swiping:select-none',
              edge.popup,
              edge.closed,
              className,
            )}
          >
            <BaseDrawer.Content className="mx-auto flex w-full max-w-lg flex-col">
              <BaseDrawer.Title className="font-display text-lg font-bold tracking-tight">
                {title}
              </BaseDrawer.Title>
              {description ? (
                <BaseDrawer.Description className="mt-2 text-bodysm leading-relaxed text-ink-muted">
                  {description}
                </BaseDrawer.Description>
              ) : null}
              <div className="mt-5">{children}</div>
            </BaseDrawer.Content>
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
}

export const DrawerClose = BaseDrawer.Close;
/** Wrap several drawers to let them stack and peek behind one another. */
export const DrawerProvider = BaseDrawer.Provider;
